import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MaterialReferenceBlockProps {
  content: {
    materialIds?: string[];
    applicationZones?: Record<string, string>;
    // backward compat
    materials?: { imageUrl?: string; name?: string; applicationZone?: string; materialId?: string }[];
  };
}

export function MaterialReferenceBlock({ content }: MaterialReferenceBlockProps) {
  const ids = content.materialIds ?? [];

  const { data: materials } = useQuery({
    queryKey: ["materials-by-ids", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("id, name, category, texture, image_url")
        .in("id", ids);
      if (error) throw error;
      return data;
    },
  });

  // backward compat for old-format blocks
  if (!ids.length && content.materials?.length) {
    return (
      <section className="mx-auto max-w-[1200px] px-6 py-16">
        <h3 className="font-serif text-2xl text-foreground mb-8">Materials Used</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.materials.map((mat, i) => (
            <div key={i} className="group">
              {mat.imageUrl && (
                <div className="aspect-square overflow-hidden mb-4">
                  <img src={mat.imageUrl} alt={mat.name || "Material"} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
                </div>
              )}
              {mat.name && <p className="font-sans text-sm font-medium text-foreground">{mat.name}</p>}
              {mat.applicationZone && <p className="font-sans text-xs text-muted-foreground mt-1">{mat.applicationZone}</p>}
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!materials?.length) return null;

  // Preserve order from materialIds
  const ordered = ids.map((id) => materials.find((m) => m.id === id)).filter(Boolean) as typeof materials;

  return (
    <section className="mx-auto max-w-[1200px] px-6 py-16">
      <h3 className="font-serif text-2xl text-foreground mb-8">Materials Used</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {ordered.map((mat) => (
          <div key={mat.id} className="group">
            {mat.image_url && (
              <div className="aspect-square overflow-hidden mb-4">
                <img
                  src={mat.image_url}
                  alt={mat.name}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </div>
            )}
            <p className="font-sans text-sm font-medium text-foreground">{mat.name}</p>
            <p className="font-sans text-xs text-muted-foreground mt-1">
              {[mat.category, mat.texture].filter(Boolean).join(" — ")}
            </p>
            {content.applicationZones?.[mat.id] && (
              <p className="font-sans text-xs text-muted-foreground/70 mt-0.5 italic">
                {content.applicationZones[mat.id]}
              </p>
            )}
            <Link
              to="/components"
              className="inline-flex items-center gap-1 mt-3 font-sans text-xs uppercase tracking-wider text-primary hover:underline"
            >
              View Material <ArrowRight size={12} />
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
